import React, { useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Alert,
  Box,
  CircularProgress,
  IconButton,
} from '@mui/material';
import UsbIcon from '@mui/icons-material/Usb';
import CloseIcon from '@mui/icons-material/Close';
import { useSerialStore } from '../stores/serialStore';
import { useLogStore } from '../stores/logStore';
import { serialService } from '../services/serialService';

interface ConnectionModalProps {
  open: boolean;
  onClose: () => void;
  skipAutoConnect?: boolean;
}

export const ConnectionModal: React.FC<ConnectionModalProps> = ({ open, onClose, skipAutoConnect = false }) => {
  const {
    baudRate,
    status,
    statusColor,
    setPort,
    setIsConnected,
    setStatus,
  } = useSerialStore();

  const addLog = useLogStore((state) => state.addLog);
  const [isConnecting, setIsConnecting] = React.useState(false);
  const autoConnectAttemptedRef = useRef(false);

  const getPortName = (port: SerialPort) => {
    const info = port.getInfo();
    if (info.usbVendorId && info.usbProductId) {
      return `USB ${info.usbVendorId.toString(16).toUpperCase()}:${info.usbProductId.toString(16).toUpperCase()}`;
    }
    return 'Serial Port';
  };

  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      const port = await serialService.requestPort();
      await serialService.connect(port, 115200);

      const portName = getPortName(port);
      setPort(port);
      setIsConnected(true);
      setStatus(`Connected to ${portName}`, 'success');
      addLog(`Connected to ${portName} at ${baudRate} baud`, 'success');

      serialService.onData((data) => {
        console.log('Serial data:', data);
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      setStatus(`Error: ${message}`, 'error');
      addLog(`Connection error: ${message}`, 'error');
    } finally {
      setIsConnecting(false);
    }
  };

  // Auto-connect if there's exactly one port available
  useEffect(() => {
    const attemptAutoConnect = async () => {
      // Only attempt once per modal open, and skip if skipAutoConnect is true
      if (!open || autoConnectAttemptedRef.current || isConnecting || skipAutoConnect) {
        return;
      }

      try {
        // Check if Web Serial API is available
        if (!('serial' in navigator)) {
          return;
        }

        // Get list of previously granted ports
        const ports = await navigator.serial.getPorts();

        // If exactly one port is available, auto-connect
        if (ports.length === 1) {
          autoConnectAttemptedRef.current = true;
          setIsConnecting(true);
          addLog('Auto-connecting to available port...', 'info');

          const port = ports[0];
          await serialService.connect(port, 115200);

          const portName = getPortName(port);
          setPort(port);
          setIsConnected(true);
          setStatus(`Connected to ${portName}`, 'success');
          addLog(`Auto-connected to ${portName} at ${baudRate} baud`, 'success');

          serialService.onData((data) => {
            console.log('Serial data:', data);
          });
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unknown error';
        setStatus(`Auto-connect failed: ${message}`, 'error');
        addLog(`Auto-connect error: ${message}`, 'error');
      } finally {
        setIsConnecting(false);
      }
    };

    attemptAutoConnect();
  }, [open, isConnecting, baudRate, setPort, setIsConnected, setStatus, addLog, skipAutoConnect]);

  // Reset auto-connect flag when modal closes
  useEffect(() => {
    if (!open) {
      autoConnectAttemptedRef.current = false;
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disablePortal
      sx={{
        position: 'absolute',
        height: '100%',
        width: '100%',
      }}
      BackdropProps={{
        sx: {
          position: 'absolute',
          height: '100%',
          width: '100%',
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
        Connect to Device
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 2 }}>
          {isConnecting ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'center' }}>
              <CircularProgress size={24} />
              <span>{autoConnectAttemptedRef.current ? 'Connecting...' : 'Requesting port access...'}</span>
            </Box>
          ) : (
            <>
              <Button
                variant="contained"
                color="primary"
                onClick={handleConnect}
                startIcon={<UsbIcon />}
                size="large"
                fullWidth
              >
                Connect to Serial Port
              </Button>

              <Alert severity={statusColor === 'success' ? 'success' : 'error'}>
                Status: {status}
              </Alert>
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
